import {ChangeDetectionStrategy, Component, Inject, TemplateRef} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';

/** What the sheet was closed with, so a screen knows whether to reload or to reset its filters. */
export type MobileFilterSheetResult = 'apply' | 'clear' | undefined;

export interface MobileFilterSheetData {
    /** The screen's own filter fields. Rendered as they are, so they keep binding to the same filter state. */
    template: TemplateRef<any>;
    /** Optional context for the template, available as let- variables. */
    context?: any;
    title?: string;
}

/**
 * Hosts a screen's filter fields in a bottom sheet.
 *
 * Below 600px the table is a stack of cards, so its thead - and with it every column filter icon - is not
 * rendered. The filters themselves are still fine; they just have nowhere to live. This gives them a place
 * without duplicating them: the screen passes the ng-template it already has, the fields keep writing to the
 * same filter state, and the sheet only decides when the screen reloads.
 *
 * Column filters normally reload on every change. On a phone that would fire a request per keystroke behind
 * a sheet the user cannot see results through, so the reload is deferred to Apply.
 *
 * Open it through MobileActionSheetService.openFilters(). Styles: _mobile-kit.scss.
 */
@Component({
    selector: 'di-mobile-filter-sheet',
    standalone: true,
    imports: [CommonModule, TranslateModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="di-mfilter-sheet">
            <div class="di-mfilter-sheet-title" *ngIf="data.title">{{ data.title }}</div>
            <div class="di-mfilter-sheet-body">
                <ng-container *ngTemplateOutlet="data.template; context: data.context"></ng-container>
            </div>
            <div class="di-mfilter-sheet-actions">
                <button type="button" class="di-mbtn" (click)="clear()">{{ 'mobileKit.clear' | translate }}</button>
                <button type="button" class="di-mbtn is-primary is-block" (click)="apply()">{{ 'mobileKit.apply' | translate }}</button>
            </div>
        </div>
    `
})
export class DiMobileFilterSheetComponent {
    constructor(
        @Inject(NZ_MODAL_DATA) public readonly data: MobileFilterSheetData,
        private readonly _modalRef: NzModalRef<DiMobileFilterSheetComponent, MobileFilterSheetResult>
    ) {}

    apply(): void {
        this._modalRef.close('apply');
    }

    clear(): void {
        this._modalRef.close('clear');
    }
}
